<template>
  <div>
    <div class="row">
      <div class="col-sm-12">
        <h1>
          General Farm Consult {{ getDairySeasonStartYear }}
          <span v-if="currentClientName">- {{ currentClientName }}</span>
        </h1>
      </div>
    </div>

    <loader v-if="isLoadingApi"></loader>

    <template v-if="dataFetched">
      <consult-opened-by-other-warning></consult-opened-by-other-warning>
      <consult-backend-errors store-namespace="currentConsult"></consult-backend-errors>

      <consult-navigation></consult-navigation>

      <div class="row">
        <div :class="[showVetNotes ? 'col-xs-9' : 'col-xs-12']">
          <vet-notes-toggle style="position: absolute; top: 30px; right: 20px"> </vet-notes-toggle>
          <component :is="currentComponent" :key="currentComponent"> </component>
        </div>

        <div class="col-xs-3" v-show="showVetNotes" style="position: sticky; top: 200px; z-index: 3">
          <div style="display: flex; flex-direction: column">
            <vet-notes field-name="gfc_vet_notes" model-name="Client" email-notification="gfc-vet-notes"> </vet-notes>
            <br />
            <br />
            <farm-services-notes field-name="notes_for_farm_services" model-name="Client"
              email-notification="notes-for-farm-services">
            </farm-services-notes>
          </div>
        </div>
      </div>

      <consult-navigation v-on:step-changed="scroll()" placement="top"></consult-navigation>
    </template>
  </div>
</template>

<script>
import { mapActions, mapGetters, mapMutations } from 'vuex'
import { generalFarmConsultComponents } from '@/components/general_farm_consult'

import currentConsult from '@/_store/current_consult.module'
import generalFarmConsult from '@/_store/general_farm_consult.module'

import ConsultBackendErrors from '@/components/ConsultBackendErrors'
import ConsultOpenedByOtherWarning from '@/components/ConsultOpenedByOtherWarning'
import ConsultNavigation from '@/components/ConsultNavigation'
import VetNotes from '@/components/VetNotes'
import FarmServicesNotes from '@/components/FarmServicesNotes'
import VetNotesToggle from '@/components/VetNotesToggle'
import { scrollToTop } from '@/_helpers'
import _ from 'lodash';

export default {
  name: 'GeneralFarmConsultPage',
  components: {
    ...generalFarmConsultComponents,
    ConsultBackendErrors,
    ConsultNavigation,
    ConsultOpenedByOtherWarning,
    FarmServicesNotes,
    VetNotes,
    VetNotesToggle,
  },
  data() {
    return {
      isLoadingApi: false,
      dataFetched: false,
    }
  },
  created() {
    Object.assign(currentConsult.state, generalFarmConsult.stateMixin)
    Object.assign(currentConsult.actions, generalFarmConsult.actionsMixin)
    Object.assign(currentConsult.mutations, generalFarmConsult.mutationsMixin)
    Object.assign(currentConsult.getters, generalFarmConsult.gettersMixin)

    if (this.$store.hasModule('currentConsult')) {
      this.$store.unregisterModule('currentConsult')
    }

    this.$store.registerModule('currentConsult', currentConsult)
    this.setClient(this.$route.params.client_id)
    this.setCurrentConsultType('generalFarmConsult')
  },
  beforeDestroy() {
    if (this.dataFetched) {
      this.backupState()
    }
  },
  mounted() {
    this.setClient(this.$route.params.client_id)
    let readOnly = Boolean(this.$route.params.history)
    this.fetchData(readOnly).then(() => {
      this.populateSectionsToBeShown()
    })
  },
  watch: {
    '$store.state': {
    handler: _.debounce(function () {
				if (this.dataFetched) {
					this.backupState()
				}
    }, 2000),
    deep: true
    }
  },
  methods: {
    ...mapMutations('currentConsult', {
      setCurrentConsultType: 'setConsultType',
    }),
    ...mapActions('currentConsult', ['backupState', 'getConsultData', 'getReadOnlyData', 'setClient', 'setFieldValue']),
    scroll: function () {
      scrollToTop()
    },
    fetchData(readOnly) {
      this.isLoadingApi = true

      let consultParams = {
        clientId: this.$route.params.client_id,
        season: this.$route.params.season,
      }

      let action = readOnly ? this.getReadOnlyData : this.getConsultData

      return action(consultParams)
        .then(() => {
          this.dataFetched = true
        })
        .catch(() => {
          this.$store.dispatch('alert/error', {
            message: 'An error occurred. Contact the administrator.',
            persist: true,
          })
          this.$router.push('/')
        })
        .finally(() => {
          this.isLoadingApi = false
        })
    },
    populateSectionsToBeShown: function () {
      let sections = ['sheep', 'beef', 'deer', 'dairy_support']
      for (let section of sections) {
        this.setFieldValue({
          fieldName: `is_${section}`,
          modelName: 'ClientDetails',
          value: this.enabledAnimals(section).length > 0,
        })
      }
    },
  },
  computed: {
    ...mapGetters('currentConsult', [
      'currentComponent',
      'currentClientName',
      'animals',
      'getFieldValue',
      'getFieldComment',
      'enabledAnimals',
      'showVetNotes',
    ]),
    getDairySeasonStartYear: function () {
      return this.$moment(this.$route.params.season).format('DD MMM Y')
    },
  },
}
</script>

<style>
.farm-fields-xl {
  width: 500px;
}

div.step-section {
  margin: 50px 5px;
}

.step-name {
  color: #337ab7;
  text-decoration: underline;
}

div.comments {
  margin: 20px;
}
</style>
